import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { Button } from './button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const;

export interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  className?: string;
  pageSizeOptions?: readonly number[];
}

function generatePageNumbers(
  currentPage: number,
  totalPages: number
): (number | 'ellipsis')[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [];

  // Always show first page
  pages.push(1);

  if (currentPage <= 3) {
    // Near the start: 1, 2, 3, 4, ..., last
    pages.push(2, 3, 4, 'ellipsis', totalPages);
  } else if (currentPage >= totalPages - 2) {
    // Near the end: 1, ..., n-3, n-2, n-1, n
    pages.push('ellipsis', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
  } else {
    // In the middle: 1, ..., c-1, c, c+1, ..., last
    pages.push('ellipsis', currentPage - 1, currentPage, currentPage + 1, 'ellipsis', totalPages);
  }

  return pages;
}

const Pagination = React.forwardRef<HTMLDivElement, PaginationProps>(
  (
    {
      page,
      pageSize,
      total,
      onPageChange,
      onPageSizeChange,
      className,
      pageSizeOptions = PAGE_SIZE_OPTIONS,
    },
    ref
  ) => {
    const { t } = useTranslation();

    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const startItem = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const endItem = Math.min(page * pageSize, total);

    const pageNumbers = React.useMemo(
      () => generatePageNumbers(page, totalPages),
      [page, totalPages]
    );

    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col items-center justify-between gap-4 px-2 py-4 sm:flex-row',
          className
        )}
      >
        {/* Results info */}
        <div className="text-sm text-muted-foreground">
          {total > 0
            ? t('pagination.showingRange', {
                from: startItem,
                to: endItem,
                total,
                defaultValue: 'Showing {{from}} to {{to}} of {{total}} results',
              })
            : t('pagination.noResults', { defaultValue: 'No results' })}
        </div>

        <div className="flex items-center gap-4">
          {/* Page size selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {t('pagination.rowsPerPage', { defaultValue: 'Rows per page' })}
            </span>
            <Select
              value={String(pageSize)}
              onValueChange={(value) => {
                onPageSizeChange(Number(value));
                onPageChange(1);
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Page navigation */}
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
              aria-label={t('common.previous')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            {pageNumbers.map((pageNum, index) =>
              pageNum === 'ellipsis' ? (
                <span
                  key={`ellipsis-${String(index)}`}
                  className="flex h-8 w-8 items-center justify-center text-sm text-muted-foreground"
                >
                  ...
                </span>
              ) : (
                <Button
                  key={pageNum}
                  variant={pageNum === page ? 'default' : 'outline'}
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onPageChange(pageNum)}
                  aria-label={t('pagination.goToPage', {
                    page: pageNum,
                    defaultValue: 'Go to page {{page}}',
                  })}
                  aria-current={pageNum === page ? 'page' : undefined}
                >
                  {pageNum}
                </Button>
              )
            )}

            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
              aria-label={t('common.next')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }
);

Pagination.displayName = 'Pagination';

export { Pagination };
